import { type FC, useState } from "react"

import Form from "@forento/ui/components/Form"

import { SubmitButton } from "~/components/Button"
import InputField from "~/components/InputField"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import { useToast } from "~/contexts/ToastContext"
import { useUser } from "~/contexts/UserContext"
import withAccessRequirement from "~/hocs/withAccessRequirement"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

const header = <PageBreadcrumb title="Tracking" path={[{ title: "Settings", link: routes.settings.index() }]} />

const TrackingSettingsPage: FC = () => {
	const user = useUser()
	const toast = useToast()
	const platform = user.user!.platform!

	const [metaPixelId, setMetaPixelId] = useState(platform.metaPixelId ?? "")
	const [metaPixelAccessToken, setMetaPixelAccessToken] = useState("")

	const update = query.platform.update.useMutation({
		async onSuccess() {
			await user.reload()
			toast.showChangesSaved("Tracking settings")
			setMetaPixelAccessToken("")
		},
	})

	return (
		<Layout>
			{header}
			<Form
				onSubmit={() =>
					update.mutate({
						metaPixelId: metaPixelId.trim() || null,
						metaPixelAccessToken: metaPixelAccessToken.trim() || undefined,
					})
				}
				className="max-w-xl space-y-6"
			>
				<p className="text-gray-600">
					Track signups and purchases with Meta (Facebook) Pixel. Enter your Pixel ID to enable client-side
					tracking. For server-side events (recommended), also add a Conversions API access token.
				</p>
				<div>
					<InputField
						label="Meta Pixel ID"
						value={metaPixelId}
						onChange={setMetaPixelId}
						placeholder="e.g. 1234567890123456"
					/>
					<p className="mt-1 text-sm text-gray-500">
						<a
							href="https://developers.facebook.com/docs/meta-pixel/get-started"
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary hover:underline"
						>
							How to create a Meta Pixel
						</a>
					</p>
				</div>
				<div>
					<InputField
						label="Conversions API access token"
						value={metaPixelAccessToken}
						onChange={setMetaPixelAccessToken}
						inputType="password"
						placeholder="Leave blank to keep current token"
						autoComplete="off"
					/>
					<p className="mt-1 text-sm text-gray-500">
						Enables server-side tracking for signups and purchases.{" "}
						<a
							href="https://developers.facebook.com/docs/marketing-api/conversions-api/get-started#get-access-token"
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary hover:underline"
						>
							Generate in Events Manager
						</a>
					</p>
				</div>
				<SubmitButton
					variant="primary"
					isLoading={update.isPending}
					isDisabled={update.isPending}
					className="cursor-pointer"
				>
					Save
				</SubmitButton>
			</Form>
		</Layout>
	)
}

export default withAccessRequirement("tracking", header, TrackingSettingsPage)
